import { draftIdPostfix } from '~/hooks/draft';

/** Feature is available on every tab of the record page. */
export const ALL_TABS = '*';

/** Marks a feature as available on the page. Feature settings can be added here later. */
type FeatureConfig = Record<string, never>;

export type RecordPage = {
  /** Tab path the drafts/changelog features are available on, or ALL_TABS. */
  path: string;
  /**
   * Permission namespace of the features below — `<prefix>.update`/`<prefix>.create`,
   * `<prefix>.changelog.list|view`.
   */
  permissionPrefix: string;
  /** Present when the record supports drafts. */
  drafts?: FeatureConfig;
  /** Present when the record supports changelog. */
  changelog?: FeatureConfig;
  /** Present when changes to the record can go through approval. */
  approvals?: FeatureConfig;
};

const RECORD_PAGES: Record<string, RecordPage> = {
  individuals: {
    path: '/show/profile',
    permissionPrefix: 'individual',
    drafts: {},
    changelog: {},
    approvals: {},
  },
  businesses: {
    path: '/show/profile',
    permissionPrefix: 'business',
    drafts: {},
    changelog: {},
    approvals: {},
  },
  pricings: {
    path: ALL_TABS,
    permissionPrefix: 'solution.pricing',
    drafts: {},
    changelog: {},
    approvals: {},
  },
  accounts: {
    path: '/show',
    permissionPrefix: 'account',
    changelog: {},
    approvals: {},
  },
  'tenant/accounts': {
    path: '/show',
    permissionPrefix: 'tenant.account',
    changelog: {},
    approvals: {},
  },
  employees: {
    path: '/show',
    permissionPrefix: 'business.employee',
    changelog: {},
    approvals: {},
  },
  team: {
    path: '/show',
    permissionPrefix: 'identity',
    changelog: {},
    approvals: {},
  },
};

const RECORD_PATH = new RegExp(
  `^/(${Object.keys(RECORD_PAGES)
    .sort((a, b) => b.length - a.length) // 'tenant/accounts' has to be matched before 'accounts'
    .join('|')})/([^/]+)(/show(?:/[^/]+)?)/?$`
);

export type MatchedRecordPage = RecordPage & {
  resource: string;
  /** Record id as it appears in the url, draft postfix included. */
  id: string;
  /** Tab the url currently points at, e.g. '/show' or '/show/profile'. */
  tabPath: string;
  isDraft: boolean;
};

export const matchRecordPage = (pathname: string): MatchedRecordPage | undefined => {
  const [, resource, id, path] = pathname.match(RECORD_PATH) ?? [];
  if (!resource) return undefined;

  const page = RECORD_PAGES[resource];
  if (page.path !== ALL_TABS && page.path !== path) return undefined;

  return { ...page, resource, id, tabPath: path, isDraft: id.endsWith(draftIdPostfix) };
};

export const getRecordPage = (resource: string): RecordPage | undefined => RECORD_PAGES[resource];

export const changelogPermissions = (page: RecordPage) => ({
  list: `${page.permissionPrefix}.changelog.list`,
  view: `${page.permissionPrefix}.changelog.view`,
});

export const draftPermissions = (page?: RecordPage) => ({
  update: `${page?.permissionPrefix}.update`,
  create: `${page?.permissionPrefix}.create`,
});
